<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline'
import ApyBrand from '@/v2/components/layout/ApyBrand.vue'
import SidebarMenuList from '@/v2/components/layout/SidebarMenuList.vue'
import { filterSidebarMenu, findActiveSectionPath, sidebarMenu } from '@/v2/data/sidebarMenu'
import { useSidebarStore } from '@/v2/stores/useSidebarStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'

const route = useRoute()
const sidebarStore = useSidebarStore()
const boostCoreStore = useBoostCorePermissionStore()

const menu = computed(() => filterSidebarMenu(sidebarMenu, boostCoreStore))

// Auto-dépliage de la section contenant la route active (spec MASTER §10) ;
// le toggle manuel reste possible, la section se ré-ouvre au prochain changement de route.
function expandActiveSection(path: string) {
  // Déplie toute la chaîne (hub niveau 1 + groupe niveau 2 repliable) menant à la page active.
  for (const id of findActiveSectionPath(sidebarMenu, path)) sidebarStore.expandSection(id)
}

watch(
  () => route.path,
  (path) => {
    expandActiveSection(path)
    // Fermeture auto du menu mobile au changement de route (UX standard)
    sidebarStore.closeMobile()
  },
)
onMounted(() => expandActiveSection(route.path))

// <dialog> natif plutôt que le drawer DaisyUI : focus trap, Esc et backdrop gratuits
// (cf MASTER §10). showModal() obligatoire — show() n'a pas de focus trap.
const dialogRef = ref<HTMLDialogElement | null>(null)
watch(
  () => sidebarStore.mobileOpen,
  (open) => {
    const dialog = dialogRef.value
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    else if (!open && dialog.open) dialog.close()
  },
)

function onBackdropClick(e: MouseEvent) {
  // Les clics sur le ::backdrop ciblent l'élément <dialog> lui-même
  if (e.target === dialogRef.value) sidebarStore.closeMobile()
}
</script>

<template>
  <!-- Desktop (lg+) : aside persistante, repliable w-60 ↔ w-16 -->
  <aside
    class="hidden h-full shrink-0 flex-col border-r border-base-300 bg-base-100 transition-[width] duration-200 ease-out motion-reduce:transition-none lg:flex"
    :class="sidebarStore.collapsed ? 'w-16' : 'w-60'"
  >
    <div
      class="flex h-14 shrink-0 items-center border-b border-base-300"
      :class="sidebarStore.collapsed ? 'justify-center px-0' : 'px-3'"
    >
      <RouterLink
        to="/accueil"
        class="flex min-h-11 items-center rounded-field focus-visible:outline-2 focus-visible:outline-primary"
        aria-label="APY — Accueil"
      >
        <ApyBrand :collapsed="sidebarStore.collapsed" />
      </RouterLink>
    </div>

    <nav
      aria-label="Navigation principale"
      class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2"
    >
      <SidebarMenuList :entries="menu" :collapsed="sidebarStore.collapsed" id-prefix="desktop" />
    </nav>

    <div class="shrink-0 border-t border-base-300 p-2">
      <button
        type="button"
        class="flex min-h-11 w-full items-center gap-2 rounded-field px-2 text-sm font-medium text-base-content/80 transition-colors hover:bg-base-200 hover:text-base-content focus-visible:outline-2 focus-visible:outline-primary motion-reduce:transition-none"
        :class="sidebarStore.collapsed ? 'justify-center px-0' : ''"
        :aria-expanded="!sidebarStore.collapsed"
        :aria-label="sidebarStore.collapsed ? 'Développer le menu' : 'Réduire le menu'"
        @click="sidebarStore.toggleCollapsed()"
      >
        <ChevronDoubleRightIcon
          v-if="sidebarStore.collapsed"
          class="h-5 w-5 shrink-0"
          aria-hidden="true"
        />
        <ChevronDoubleLeftIcon v-else class="h-5 w-5 shrink-0" aria-hidden="true" />
        <span v-if="!sidebarStore.collapsed">Réduire</span>
      </button>
    </div>
  </aside>

  <!-- Mobile (<lg) : dialog natif ouvert via le burger du header -->
  <dialog
    ref="dialogRef"
    class="m-0 h-dvh max-h-dvh w-72 max-w-[80vw] bg-base-100 p-0 backdrop:bg-black/50 lg:hidden"
    aria-label="Navigation principale"
    @click="onBackdropClick"
    @close="sidebarStore.closeMobile()"
  >
    <div class="flex h-full flex-col">
      <div class="flex h-14 shrink-0 items-center justify-between border-b border-base-300 px-3">
        <RouterLink
          to="/accueil"
          class="flex min-h-11 items-center rounded-field focus-visible:outline-2 focus-visible:outline-primary"
          aria-label="APY — Accueil"
        >
          <ApyBrand />
        </RouterLink>
        <button
          type="button"
          class="btn btn-square btn-ghost"
          aria-label="Fermer le menu"
          @click="sidebarStore.closeMobile()"
        >
          <XMarkIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <nav aria-label="Navigation principale" class="min-h-0 flex-1 overflow-y-auto p-2">
        <SidebarMenuList :entries="menu" :collapsed="false" id-prefix="mobile" />
      </nav>
    </div>
  </dialog>
</template>
